/**
 * /admin/cost-centers — Apptio-grade Cost Center Hierarchy.
 *
 * Renders the full chart-of-accounts tree (parent → child → grandchild) with:
 *   - rollup_cost_30d per node (sum of self + descendants)
 *   - budget_used_pct + over-budget badge
 *   - inline create / delete actions
 *   - CSV chart-of-accounts import
 *   - drift-alerts panel (children that exceeded threshold × budget)
 *   - AI auto-suggest parent for an untagged spend cluster
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  TreePine, Plus, Trash2, AlertTriangle, FileSpreadsheet, Sparkles, RefreshCw,
  ChevronRight, ChevronDown, DollarSign,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

// ─── Tree node renderer (recursive) ───────────────────────────────────────
function TreeNode({ node, depth, onDelete, onAddChild, expandedMap, toggleExpand }) {
  const isExpanded = expandedMap[node.node_id] !== false; // default expanded
  const hasChildren = node.children && node.children.length > 0;
  const budget = node.budget_monthly_usd || 0;
  const cost = node.rollup_cost_30d || 0;
  const usedPct = node.budget_used_pct || 0;
  const overBudget = node.over_budget;
  const heat = overBudget ? 'rose' : usedPct >= 70 ? 'amber' : 'emerald';
  const heatBorder = overBudget
    ? 'border-rose-500/40'
    : usedPct >= 70
    ? 'border-amber-500/40'
    : 'border-emerald-500/20';
  const heatText = overBudget ? 'text-rose-300' : usedPct >= 70 ? 'text-amber-300' : 'text-emerald-300';
  return (
    <>
      <tr className={`border-b border-emerald-500/10 hover:bg-slate-800 ${heatBorder}`} data-testid={`cc-node-row-${node.node_id}`}>
        <td className="py-3 px-2" style={{ paddingLeft: `${0.5 + depth * 1.5}rem` }}>
          <div className="flex items-center gap-2">
            {hasChildren ? (
              <button onClick={() => toggleExpand(node.node_id)} className="text-slate-300 hover:text-white" data-testid={`cc-toggle-${node.node_id}`}>
                {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            ) : (
              <span className="inline-block w-4" />
            )}
            <div>
              <div className="text-slate-900 font-semibold">{node.name}</div>
              {node.code && <div className="text-[10px] font-mono text-cyan-300">{node.code}</div>}
            </div>
          </div>
        </td>
        <td className="py-3 px-2 text-right">
          <span className="text-base font-bold text-slate-900 tabular-nums">
            ${budget.toLocaleString('en-US', { maximumFractionDigits: 0 })}
          </span>
        </td>
        <td className="py-3 px-2 text-right">
          <span className={`text-base font-bold tabular-nums ${heatText}`}>
            ${cost.toLocaleString('en-US', { maximumFractionDigits: 0 })}
          </span>
        </td>
        <td className="py-3 px-2 text-right">
          {budget > 0 ? (
            <Badge className={`text-[10px] font-mono ${
              overBudget ? 'bg-rose-500/20 text-rose-200 border border-rose-400/40'
                : usedPct >= 70 ? 'bg-amber-500/20 text-amber-200 border border-amber-400/40'
                : 'bg-emerald-500/20 text-emerald-200 border border-emerald-400/40'
            }`}>
              {usedPct.toFixed(0)}%
            </Badge>
          ) : (
            <span className="text-xs text-slate-600">—</span>
          )}
        </td>
        <td className="py-3 px-2 text-xs text-slate-700">
          {node.owner_email || <span className="text-slate-600 italic">(none)</span>}
        </td>
        <td className="py-3 px-2 text-right">
          <div className="flex items-center justify-end gap-1">
            <button onClick={() => onAddChild(node)} title="Add child" className="text-emerald-300 hover:text-emerald-200" data-testid={`cc-addchild-${node.node_id}`}>
              <Plus className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => onDelete(node)} title="Delete" className="text-rose-300 hover:text-rose-200" data-testid={`cc-delete-${node.node_id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </td>
      </tr>
      {isExpanded && hasChildren && node.children.map((c) => (
        <TreeNode key={c.node_id} node={c} depth={depth + 1}
                  onDelete={onDelete} onAddChild={onAddChild}
                  expandedMap={expandedMap} toggleExpand={toggleExpand} />
      ))}
    </>
  );
}

// ─── Inline create form ───────────────────────────────────────────────────
function CreateNodeForm({ parent, onCreate, onCancel }) {
  const [form, setForm] = useState({
    name: '', code: '', budget_monthly_usd: '', owner_email: '', tag_match: '',
  });
  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name.trim(),
      code: form.code.trim() || undefined,
      parent_id: parent ? parent.node_id : null,
      budget_monthly_usd: parseFloat(form.budget_monthly_usd) || 0,
      owner_email: form.owner_email.trim() || undefined,
      tag_match: form.tag_match.split(',').map((t) => t.trim()).filter(Boolean),
    };
    await onCreate(payload);
  };
  return (
    <form onSubmit={submit} className="bg-slate-900 border border-emerald-500/30 rounded-xl p-4 mb-4" data-testid="cc-create-form">
      <div className="text-sm font-bold text-slate-900 mb-3">
        {parent ? <>Add child under <code className="text-emerald-300">{parent.name}</code></> : 'Add root cost center'}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <Input data-testid="cc-form-name" placeholder="Name (required)" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required
               className="bg-slate-950 border-emerald-500/20 text-white" />
        <Input data-testid="cc-form-code" placeholder="GL code (e.g. ENG-PLAT)" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
               className="bg-slate-950 border-emerald-500/20 text-white" />
        <Input data-testid="cc-form-budget" type="number" min="0" placeholder="Monthly budget (USD)" value={form.budget_monthly_usd}
               onChange={(e) => setForm((f) => ({ ...f, budget_monthly_usd: e.target.value }))}
               className="bg-slate-950 border-emerald-500/20 text-white" />
        <Input data-testid="cc-form-owner" type="email" placeholder="Owner email (optional)" value={form.owner_email}
               onChange={(e) => setForm((f) => ({ ...f, owner_email: e.target.value }))}
               className="bg-slate-950 border-emerald-500/20 text-white" />
      </div>
      <Input data-testid="cc-form-tags" placeholder="Tag match (comma-separated, e.g. team:platform, cost_center:eng)" value={form.tag_match}
             onChange={(e) => setForm((f) => ({ ...f, tag_match: e.target.value }))}
             className="bg-slate-950 border-emerald-500/20 text-white mb-3" />
      <div className="flex gap-2">
        <Button type="submit" className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="cc-create-submit">
          <Plus className="w-4 h-4 mr-1.5" /> Create
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid="cc-create-cancel">
          Cancel
        </Button>
      </div>
    </form>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────
export default function CostCenters() {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedMap, setExpandedMap] = useState({});
  const [creatingUnder, setCreatingUnder] = useState(undefined); // undefined = closed, null = root
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState(SAMPLE_CSV);
  const [importReplace, setImportReplace] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [drift, setDrift] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [suggestForm, setSuggestForm] = useState({ name: '', tags: '' });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/cost-centers/tree`, { headers: authHeaders() });
      if (r.ok) setTree(await r.json());
      const d = await fetch(`${API}/api/cost-centers/drift-alerts`, { headers: authHeaders() });
      if (d.ok) setDrift(await d.json());
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const toggleExpand = (id) => setExpandedMap((m) => ({ ...m, [id]: m[id] === false ? true : false }));

  const handleCreate = async (payload) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/cost-centers/nodes`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(payload),
      });
      if (r.ok) {
        setCreatingUnder(undefined);
        await refresh();
      } else {
        const err = await r.json();
        alert(`Create failed: ${err.detail || r.status}`);
      }
    } finally { setBusy(false); }
  };

  const handleDelete = async (node) => {
    if (!window.confirm(`Delete "${node.name}" and ALL its descendants?`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/cost-centers/nodes/${node.node_id}?cascade=true`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (r.ok) await refresh();
      else alert('Delete failed');
    } finally { setBusy(false); }
  };

  const handleImport = async () => {
    setBusy(true);
    setImportResult(null);
    try {
      const r = await fetch(`${API}/api/cost-centers/import-csv`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ csv_text: importText, replace_existing: importReplace }),
      });
      const d = await r.json();
      if (r.ok) {
        setImportResult({ ok: true, ...d });
        await refresh();
      } else {
        setImportResult({ ok: false, error: d.detail || `HTTP ${r.status}` });
      }
    } finally { setBusy(false); }
  };

  const handleSuggest = async () => {
    setBusy(true);
    setSuggestion(null);
    try {
      const tags = suggestForm.tags.split(',').map((t) => t.trim()).filter(Boolean);
      const r = await fetch(`${API}/api/cost-centers/auto-suggest-parent`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ spend_cluster_name: suggestForm.name, sample_tags: tags }),
      });
      if (r.ok) setSuggestion(await r.json());
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 -mt-px">
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="cost-centers-page">
      <Helmet><title>Cost Center Hierarchy · CARTIEAI</title></Helmet>

      {/* Header */}
      <div className="aurora-hero rounded-2xl px-4 py-6 mb-8 -mx-4">
        <div className="flex items-start gap-4 mb-4">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-emerald-500 bg-gradient-to-br from-emerald-500 to-cyan-600 items-center justify-center shadow-xl shadow-emerald-500/40 shrink-0">
            <TreePine className="w-7 h-7 text-white drop-shadow-sm" />
          </div>
          <div className="flex-1">
            <Badge className="bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-[10px] font-mono tracking-widest mb-2">FINANCE · CHART OF ACCOUNTS</Badge>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 drop-shadow-[0_2px_18px_rgba(16,185,129,0.25)]">
              Cost Center <span className="text-emerald-400">Hierarchy</span>
            </h1>
          </div>
          <Button onClick={refresh} disabled={loading || busy} variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid="cc-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
        <p className="text-lg text-slate-100 max-w-3xl">
          Apptio-grade parent/child cost tree with <span className="text-emerald-400 font-bold">automatic 30-day rollup</span>, budget drift alerts, and CSV chart-of-accounts import. Tag your cloud spend with <code className="text-cyan-300">cost_center=…</code> and we'll bubble the totals up the tree for you.
        </p>
      </div>

      {/* Top KPIs — hero tile is white, others stay dark for hierarchy */}
      {tree && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="cc-kpi-grid">
          <Card className="surface-hero relative overflow-hidden surface-hero-accent-strip"><CardContent className="p-4">
            <div className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest mb-2">Total budget · monthly</div>
            <div className="text-3xl font-bold text-slate-900 tabular-nums">${tree.total_budget_usd.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
            <div className="text-[10px] text-slate-500 mt-1">{tree.node_count} cost centers tracked</div>
          </CardContent></Card>
          <Card className="bg-slate-950 text-slate-100 border border-emerald-500/20"><CardContent className="p-4">
            <div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-2">Cost centers</div>
            <div className="text-3xl font-bold text-white tabular-nums">{tree.node_count}</div>
          </CardContent></Card>
          <Card className="bg-slate-950 text-slate-100 border border-emerald-500/20"><CardContent className="p-4">
            <div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-2">Actual · last 30d</div>
            <div className="text-3xl font-bold text-white tabular-nums">${tree.total_actual_usd.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
          </CardContent></Card>
          <Card className={`bg-slate-950 text-slate-100 border ${(drift?.alert_count || 0) > 0 ? 'border-rose-500/30' : 'border-emerald-500/20'}`}><CardContent className="p-4">
            <div className={`text-[10px] font-bold uppercase tracking-widest mb-2 ${(drift?.alert_count || 0) > 0 ? 'text-rose-300' : 'text-emerald-300'}`}>Drift alerts</div>
            <div className={`text-3xl font-bold tabular-nums ${(drift?.alert_count || 0) > 0 ? 'text-rose-300' : 'text-emerald-300'}`}>{drift?.alert_count || 0}</div>
          </CardContent></Card>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button onClick={() => setCreatingUnder(null)} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="cc-add-root-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add root cost center
        </Button>
        <Button onClick={() => setShowImport(!showImport)} variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid="cc-import-toggle-btn">
          <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Import CSV chart of accounts
        </Button>
      </div>

      {/* CSV import card */}
      {showImport && (
        <Card className="bg-slate-950 text-slate-100 border border-emerald-500/30 mb-6" data-testid="cc-import-card">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-emerald-400" /> Paste your chart of accounts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-300 mb-3">
              Columns: <code className="text-emerald-300">name, code, parent_code, budget_monthly_usd, owner_email, tag_match</code>. The <code className="text-emerald-300">parent_code</code> column wires children to their parent. Blank = root node.
            </p>
            <Textarea data-testid="cc-import-textarea" value={importText} onChange={(e) => setImportText(e.target.value)}
                      rows={10} className="dark-input bg-slate-900 border-emerald-500/20 text-slate-100 font-mono text-xs mb-3" />
            <label className="text-xs text-slate-300 flex items-center gap-2 mb-3">
              <input type="checkbox" checked={importReplace} onChange={(e) => setImportReplace(e.target.checked)} data-testid="cc-import-replace-checkbox" />
              Replace ALL existing cost centers first (destructive)
            </label>
            <div className="flex gap-2">
              <Button onClick={handleImport} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="cc-import-submit">
                {busy ? 'Importing…' : 'Import'}
              </Button>
              <Button variant="outline" onClick={() => setShowImport(false)} className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">
                Close
              </Button>
            </div>
            {importResult && (
              <div className={`mt-3 text-xs ${importResult.ok ? 'text-emerald-300' : 'text-rose-300'}`} data-testid="cc-import-result">
                {importResult.ok
                  ? `✓ Inserted ${importResult.inserted} nodes · ${importResult.parents_wired} parents wired${importResult.skipped?.length ? ` · ${importResult.skipped.length} skipped` : ''}`
                  : `✗ ${importResult.error}`}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Inline create form */}
      {creatingUnder !== undefined && (
        <CreateNodeForm
          parent={creatingUnder}
          onCreate={handleCreate}
          onCancel={() => setCreatingUnder(undefined)}
        />
      )}

      {/* Tree */}
      <Card className="bg-slate-950 text-slate-100 border border-emerald-500/30 mb-6" data-testid="cc-tree-card">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <TreePine className="w-5 h-5 text-emerald-400" /> Tree
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">{[0,1,2,3].map((i) => <div key={i} className="h-12 bg-slate-800 rounded animate-pulse" />)}</div>
          ) : !tree || tree.node_count === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="w-10 h-10 text-emerald-400/50 mx-auto mb-2" />
              <div className="text-slate-300 mb-3">No cost centers yet. Add a root to get started.</div>
              <Button onClick={() => setCreatingUnder(null)} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="cc-empty-cta">
                <Plus className="w-4 h-4 mr-1.5" /> Add first cost center
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase text-cyan-300 border-b border-emerald-500/20">
                  <tr>
                    <th className="text-left py-2 px-2 font-bold">Cost center</th>
                    <th className="text-right py-2 px-2 font-bold">Budget</th>
                    <th className="text-right py-2 px-2 font-bold">Actual · 30d</th>
                    <th className="text-right py-2 px-2 font-bold">Used</th>
                    <th className="text-left py-2 px-2 font-bold">Owner</th>
                    <th className="text-right py-2 px-2 font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tree.tree.map((root) => (
                    <TreeNode key={root.node_id} node={root} depth={0}
                              onDelete={handleDelete} onAddChild={(n) => setCreatingUnder(n)}
                              expandedMap={expandedMap} toggleExpand={toggleExpand} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Drift alerts */}
      {drift && drift.alert_count > 0 && (
        <Card className="bg-slate-950 text-slate-100 border border-rose-500/30 mb-6" data-testid="cc-drift-card">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-400" /> Budget drift alerts · ≥{(drift.threshold * 100).toFixed(0)}% used
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {drift.alerts.slice(0, 10).map((a) => (
                <div key={a.node_id} className="flex items-center justify-between bg-rose-500/5 border-l-2 border-rose-500/60 px-3 py-2 rounded" data-testid={`cc-drift-row-${a.node_id}`}>
                  <div>
                    <div className="text-sm font-semibold text-white">{a.path}</div>
                    <div className="text-[10px] text-slate-400">Owner: {a.owner_email || '(none)'}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-rose-300 tabular-nums">${a.actual_cost_30d_usd.toLocaleString('en-US')} / ${a.budget_monthly_usd.toLocaleString('en-US')}</div>
                    <Badge className={`text-[10px] font-mono ${
                      a.severity === 'critical' ? 'bg-rose-600/30 text-rose-100 border border-rose-500/60'
                        : a.severity === 'high' ? 'bg-rose-500/20 text-rose-200 border border-rose-400/40'
                        : 'bg-amber-500/20 text-amber-200 border border-amber-400/40'
                    }`}>
                      {a.budget_used_pct.toFixed(0)}% · {a.severity.toUpperCase()}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI suggest parent */}
      <Card className="bg-slate-950 text-slate-100 border border-emerald-500/30 mb-6" data-testid="cc-suggest-card">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-400" /> Auto-suggest parent
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-300 mb-3">
            Paste an untagged spend cluster name + sample tags. CARTIE's tag-inferencer suggests the best parent cost center.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <Input data-testid="cc-suggest-name" placeholder="Spend cluster name (e.g. GitHub Copilot bill)"
                   value={suggestForm.name} onChange={(e) => setSuggestForm((f) => ({ ...f, name: e.target.value }))}
                   className="bg-slate-900 border-emerald-500/20 text-white" />
            <Input data-testid="cc-suggest-tags" placeholder="Sample tags (comma-separated, e.g. team:engineering, tool:copilot)"
                   value={suggestForm.tags} onChange={(e) => setSuggestForm((f) => ({ ...f, tags: e.target.value }))}
                   className="bg-slate-900 border-emerald-500/20 text-white" />
          </div>
          <Button onClick={handleSuggest} disabled={busy || !suggestForm.name.trim()} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="cc-suggest-btn">
            <Sparkles className="w-4 h-4 mr-1.5" /> Suggest parent
          </Button>
          {suggestion && (
            <div className="mt-4 bg-slate-900 border border-emerald-500/30 rounded p-3" data-testid="cc-suggest-result">
              {suggestion.suggestion ? (
                <div>
                  <div className="text-xs text-emerald-300 uppercase font-mono mb-1">Suggested parent · confidence: {suggestion.confidence}</div>
                  <div className="text-sm font-bold text-white">{suggestion.suggestion.name} {suggestion.suggestion.code && <code className="text-cyan-300 text-[10px] ml-1">{suggestion.suggestion.code}</code>}</div>
                  <div className="text-xs text-slate-300 italic mt-1">{suggestion.reason}</div>
                </div>
              ) : (
                <div className="text-xs text-slate-400 italic">{suggestion.reason || 'No suggestion'}</div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </div>
  );
}

const SAMPLE_CSV = `name,code,parent_code,budget_monthly_usd,owner_email,tag_match
Engineering,ENG,,50000,eng@cartie.ai,team:engineering
Marketing,MKT,,10000,mkt@cartie.ai,team:marketing
Platform,ENG-PLAT,ENG,30000,plat@cartie.ai,team:platform
Mobile,ENG-MOB,ENG,12000,,team:mobile
Ads,MKT-ADS,MKT,7000,,channel:ads
SaaS Tools,MKT-SAAS,MKT,2500,,channel:saas`;
